:root {
  color: #14211d;
  background: #f5f1e8;
  font-family:
    Inter,
    ui-sans-serif,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  min-width: 320px;
  min-height: 100vh;
}

button,
select {
  font: inherit;
}

button {
  align-items: center;
  border: 1px solid #cfc8ba;
  border-radius: 8px;
  background: #fffdf7;
  color: #14211d;
  cursor: pointer;
  display: inline-flex;
  font-weight: 750;
  gap: 0.5rem;
  min-height: 42px;
  padding: 0.65rem 0.9rem;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

button svg {
  height: 18px;
  width: 18px;
}

button.primary {
  background: #1f6f54;
  border-color: #1f6f54;
  color: #ffffff;
}

button.danger {
  background: #a63d40;
  border-color: #a63d40;
  color: #ffffff;
}

select {
  border: 1px solid #cfc8ba;
  border-radius: 8px;
  min-height: 44px;
  padding: 0.6rem 0.75rem;
  width: 100%;
}

audio {
  width: 100%;
}

.shell {
  background:
    linear-gradient(120deg, rgba(31, 111, 84, 0.08), transparent 44%),
    linear-gradient(180deg, #fffaf0 0%, #f5f1e8 100%);
  min-height: 100vh;
  padding: 28px;
}

.workspace {
  margin: 0 auto;
  max-width: 1180px;
}

.masthead {
  align-items: end;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 20px;
}

.eyebrow {
  color: #6d5d42;
  font-size: 0.78rem;
  font-weight: 850;
  letter-spacing: 0;
  margin: 0 0 0.4rem;
  text-transform: uppercase;
}

h1 {
  font-size: clamp(2.4rem, 7vw, 5rem);
  line-height: 0.95;
  margin: 0;
}

h2 {
  font-size: 1rem;
  margin: 0;
}

.lede {
  color: #4f5b55;
  font-size: 1.05rem;
  max-width: 620px;
}

.privacy-pill {
  background: #102820;
  border-radius: 999px;
  color: #fffaf0;
  font-size: 0.88rem;
  font-weight: 800;
  padding: 0.65rem 0.9rem;
  white-space: nowrap;
}

.practice-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 0.95fr) minmax(340px, 1.05fr);
}

.panel {
  background: rgba(255, 253, 247, 0.9);
  border: 1px solid #ddd4c2;
  border-radius: 8px;
  box-shadow: 0 18px 46px rgba(32, 29, 20, 0.08);
  padding: 20px;
}

.section-header {
  align-items: center;
  display: flex;
  gap: 0.55rem;
  margin-bottom: 16px;
}

.section-header svg {
  color: #1f6f54;
  height: 21px;
  width: 21px;
}

.field-label {
  color: #5d635f;
  display: block;
  font-size: 0.84rem;
  font-weight: 800;
  margin-bottom: 0.45rem;
}

blockquote {
  border-left: 4px solid #e3b23c;
  font-size: 1.6rem;
  font-weight: 820;
  line-height: 1.18;
  margin: 18px 0;
  padding-left: 14px;
}

.focus-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.focus-row span {
  background: #e5f2ec;
  border: 1px solid #bad6ca;
  border-radius: 999px;
  color: #0f513f;
  font-weight: 850;
  padding: 0.35rem 0.6rem;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.error {
  background: #fff0ed;
  border: 1px solid #e5aaa1;
  border-radius: 8px;
  color: #842029;
  padding: 0.75rem;
}

.score {
  align-items: baseline;
  display: flex;
  gap: 0.5rem;
  margin-bottom: 14px;
}

.score span {
  color: #1f6f54;
  font-size: 3.4rem;
  font-weight: 900;
  line-height: 1;
}

.score small,
.phoneme small {
  color: #617069;
  font-weight: 750;
}

.phoneme-list {
  display: grid;
  gap: 10px;
}

.phoneme {
  border: 1px solid #ddd4c2;
  border-left: 5px solid #aeb8b2;
  border-radius: 8px;
  display: grid;
  gap: 6px;
  grid-template-columns: 1fr auto;
  padding: 12px;
}

.phoneme.good {
  border-left-color: #1f6f54;
}

.phoneme.watch {
  border-left-color: #d08a1f;
}

.phoneme.drill {
  border-left-color: #a63d40;
}

.phoneme div {
  align-items: baseline;
  display: flex;
  gap: 0.55rem;
}

.phoneme strong {
  font-size: 1.2rem;
}

.phoneme p,
.history p {
  grid-column: 1 / -1;
  margin: 0;
}

.chart-panel,
.history {
  margin-top: 18px;
}

.formant-chart {
  display: block;
  height: auto;
  max-height: 440px;
  width: 100%;
}

.formant-chart rect {
  fill: #fbf8ef;
}

.formant-chart line {
  stroke: #ddd4c2;
  stroke-width: 1;
}

.formant-chart text {
  fill: #66726d;
  font-size: 13px;
}

.user-dot {
  fill: #1f6f54;
  opacity: 0.45;
}

.target-dot {
  fill: #e3b23c;
  stroke: #5a4110;
  stroke-width: 2;
}

.target-label {
  fill: #14211d;
  font-size: 18px;
  font-weight: 900;
}

.history h2 {
  margin-bottom: 12px;
}

.history-list {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.history-list button {
  justify-content: space-between;
  text-align: left;
}

@media (max-width: 820px) {
  .shell {
    padding: 16px;
  }

  .masthead {
    align-items: start;
    flex-direction: column;
  }

  .privacy-pill {
    white-space: normal;
  }

  .practice-grid {
    grid-template-columns: 1fr;
  }

  blockquote {
    font-size: 1.25rem;
  }
}
